import { useEffect, useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/common/hooks/useAuth'
import { useImpersonation } from '@/common/hooks/useImpersonation'
import { LoginForm } from '@/common/components/login-form/LoginForm'
import { Dashboard } from '@/common/components/dashboard/Dashboard'
import { Navbar } from '@/common/components/navbar'
import { BrandMark } from '@/common/components/brand-mark'
import { colors } from '@/core/styles/tokens'

export const Route = createFileRoute('/')({
  component: HomePage,
})

const ERROR_MESSAGES: Record<string, string> = {
  session_expired: 'Your session expired. Please sign in again.',
  auth_failed: 'Authentication failed. Please try again.',
}

function HomePage() {
  const { isLoading, isAuthenticated, user } = useAuth()
  const { isImpersonating, impersonatedRoles } = useImpersonation()
  const navigate = useNavigate()

  // Determine effective roles: impersonated user's roles or the real user's roles
  const effectiveRoles = useMemo(
    () => (isImpersonating ? impersonatedRoles : (user?.roles ?? [])),
    [isImpersonating, impersonatedRoles, user?.roles],
  )

  // Redirect based on effective roles:
  // - Not impersonating: admin → /org, manager → /manager
  // - Impersonating a manager: → /manager (see their view)
  // - Impersonating a regular employee: stay on / (employee dashboard)
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      if (!isImpersonating && effectiveRoles.includes('admin')) {
        navigate({ to: '/org', replace: true })
      } else if (effectiveRoles.includes('manager')) {
        navigate({ to: '/manager', replace: true })
      }
    }
  }, [isLoading, isAuthenticated, isImpersonating, effectiveRoles, navigate])

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    const params = new URLSearchParams(window.location.search)
    const errorCode = params.get('error')
    const errorMessage = errorCode ? ERROR_MESSAGES[errorCode] ?? null : null

    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.title}><BrandMark /></div>
          <p style={styles.subtitle}>Sign in to continue</p>
        </div>
        <LoginForm error={errorMessage} />
        <footer style={styles.footer}>
          <p style={styles.footerText}>
            Orchard Timesheet is the official employee time-tracking
            application of Orchard.
          </p>
          <p style={styles.footerText}>
            Need help signing in?{' '}
            <a style={styles.footerLink} href="https://orchardhomes.slack.com/archives/C0B4YSFMKF1">
              Message #timesheet-support on Slack
            </a>
            .
          </p>
        </footer>
      </div>
    )
  }

  // If the useEffect above will redirect, show a loading state.
  // Admins/managers redirect to their dashboards; impersonating a manager also redirects.
  const willRedirect =
    (!isImpersonating && effectiveRoles.includes('admin')) ||
    effectiveRoles.includes('manager')
  if (willRedirect) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Redirecting...</div>
      </div>
    )
  }

  return (
    <>
      <Navbar />
      <Dashboard />
    </>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    padding: '2rem',
    fontFamily: 'SharpGrotesk, system-ui, -apple-system, sans-serif',
    backgroundColor: colors.lightBirch,
  },
  header: {
    marginBottom: '2rem',
  },
  title: {
    margin: 0,
  },
  subtitle: {
    color: colors.gray70,
    marginTop: '0.5rem',
  },
  footer: {
    maxWidth: '400px',
    margin: '1.5rem auto 0',
    textAlign: 'center',
  },
  footerText: {
    color: colors.gray70,
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    margin: '0.25rem 0',
  },
  footerLink: {
    color: colors.sea,
    textDecoration: 'underline',
  },
  loading: {
    padding: '4rem',
    color: colors.gray70,
  },
}
